// ========== MAPPING MODELS ==========
// After an extraction process completes, the backend dispatches a mapping job.
// Mapping progress is delivered over a single org-wide WebSocket (separate
// from the per-process extraction socket); each message carries `process_id`
// so every result in the extraction process tracks the same mapping job.

export const MappingWebSocketMessageType = {
  Authenticate: "authenticate",
  Ping: "ping",
  Progress: "mapping.job.progress",
  Completed: "mapping.job.completed",
  Failed: "mapping.job.failed",
} as const;

export type MappingWebSocketMessageType =
  (typeof MappingWebSocketMessageType)[keyof typeof MappingWebSocketMessageType];

// Raw message shape as it arrives on the wire from the mapping notifier.
export interface IMappingNotificationMessage {
  type: string;
  job_id?: string | null;
  plugin_id?: string | null;
  service?: string | null;
  status?: string | null;
  rows_total?: number | null;
  rows_done?: number | null;
  failed_files?: number | null;
  process_id?: string | null;
}

// Backend job status as returned by the mapping REST API. These are the
// raw lifecycle states; the UI collapses them into `MappingViewStatus`.
export const MappingJobBackendStatus = {
  AwaitingUpload: "AwaitingUpload",
  Parsing: "Parsing",
  Running: "Running",
  Completed: "Completed",
  Failed: "Failed",
} as const;

export type MappingJobBackendStatus =
  (typeof MappingJobBackendStatus)[keyof typeof MappingJobBackendStatus];

// One job summary from GET /jobs?process_id=..., with the requested process ID
// added (the summaries do not carry it).
export interface IMappingJobStatusResponse {
  job_id: string;
  process_id: string | null;
  status: string;
  rows_total?: number | null;
  rows_done?: number | null;
  failed_files?: number | null;
  plugin_id?: string | null;
  service?: string | null;
}

export interface IMappingJobListResponse {
  jobs: Array<Omit<IMappingJobStatusResponse, "process_id">>;
  cursor: string | null;
}

// UI-facing status for a mapping job attached to an extraction process.
export const MappingViewStatus = {
  Running: "running",
  Completed: "completed",
  Failed: "failed",
} as const;

export type MappingViewStatus =
  (typeof MappingViewStatus)[keyof typeof MappingViewStatus];

// Process-level mapping state rendered alongside each extraction result item.
export interface IMappingJobViewModel {
  jobId: string;
  processId: string;
  status: MappingViewStatus;
  rowsDone: number;
  rowsTotal: number;
  failedFiles: number;
  service?: string | null;
  pluginId?: string | null;
}

// Response from POST /processes/{id}/dispatch-mapping — the on-demand
// "Run mapping" trigger. The job is created asynchronously downstream.
export interface IMappingDispatchResponse {
  status: string;
  process_id: string;
  file_count?: number;
}
